import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getMembershipTypes, getCurrentMembership, createPaymentIntent, confirmPayment, checkUpgradeEligibility } from '../services/api';
import Alert from '../components/common/Alert';

// Load Stripe
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
console.log('Stripe key loaded:', stripeKey ? 'Present' : 'Missing');
const stripePromise = stripeKey ? loadStripe(stripeKey) : Promise.resolve(null);

// Payment form component with Stripe Elements
const UpgradePaymentForm = ({ selectedPlan, agreeToTerms, setAgreeToTerms, onSubmit, processing, error }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isStripeReady, setIsStripeReady] = useState(false);

  useEffect(() => {
    if (stripe && elements) {
      setIsStripeReady(true);
    }
  }, [stripe, elements]);
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    address: {
      line1: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US'
    }
  });

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBillingDetails(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setBillingDetails(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !agreeToTerms) return;
    
    onSubmit({
      stripe,
      elements,
      billingDetails,
      cardElement: elements.getElement(CardNumberElement)
    });
  };

  const cardStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        lineHeight: '24px',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      focus: {
        color: '#424770',
      },
      invalid: {
        color: '#9e2146',
      },
      complete: {
        color: '#424770',
      },
    },
  };

  if (!isStripeReady) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">Loading payment form...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Billing Information */}
      <div className="space-y-4 mb-6">
        <h4 className="text-md font-medium text-gray-900">Billing Information</h4>
        <div className="grid grid-cols-1 gap-4">
          <input
            type="text"
            placeholder="Full Name"
            value={billingDetails.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
          <input
            type="text"
            placeholder="Billing Address"
            value={billingDetails.address.line1}
            onChange={(e) => handleInputChange('address.line1', e.target.value)}
            className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="City"
              value={billingDetails.address.city}
              onChange={(e) => handleInputChange('address.city', e.target.value)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <input
              type="text"
              placeholder="State"
              value={billingDetails.address.state}
              onChange={(e) => handleInputChange('address.state', e.target.value)}
              className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <input
            type="text"
            placeholder="ZIP Code"
            value={billingDetails.address.postal_code}
            onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
            className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required
          />
        </div>
      </div>

      {/* Payment Information */}
      <div className="space-y-4 mb-6">
        <h4 className="text-md font-medium text-gray-900">Card Information</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Card Number</label>
            <div className="border border-gray-300 rounded-md p-3 min-h-[48px] flex items-center focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
              <CardNumberElement 
                options={cardStyle}
                className="w-full"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Expiration Date</label>
              <div className="border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <CardExpiryElement options={cardStyle} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Security Code</label>
              <div className="border border-gray-300 rounded-md p-3 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <CardCvcElement options={cardStyle} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert type="error" message={error} onClose={() => setPaymentError('')} />
      )}

      {/* Terms Agreement */}
      <div className="border-t pt-6 mb-6">
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              type="checkbox"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
          <div className="ml-3">
            <label className="text-sm text-gray-700">
              I agree to the{' '}
              <a href="/terms" className="text-blue-600 hover:text-blue-800 underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-blue-600 hover:text-blue-800 underline">
                Privacy Policy
              </a>
            </label>
          </div>
        </div>
      </div>

      {/* Subscribe Button */}
      <button
        type="submit"
        disabled={!stripe || !selectedPlan || !agreeToTerms || processing}
        className={`w-full py-3 px-4 rounded-md font-semibold text-lg transition-colors ${
          selectedPlan && agreeToTerms && !processing
            ? 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
      >
        {processing ? 'Processing...' : 'Subscribe'}
      </button>
    </form>
  );
};

function UpgradePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [currentMembership, setCurrentMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [autoRenew, setAutoRenew] = useState(true);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Prorated/credited upgrade specific states (Quarterly -> Annual, Annual -> Family)
  const [isQuarterlyToAnnualUpgrade, setIsQuarterlyToAnnualUpgrade] = useState(false);
  const [isAnnualToFamilyUpgrade, setIsAnnualToFamilyUpgrade] = useState(false);
  const [upgradeDetails, setUpgradeDetails] = useState(null);
  const isUpgradeFlow = isQuarterlyToAnnualUpgrade || isAnnualToFamilyUpgrade;

  // Get suggested plan from navigation state
  const suggestedPlan = location.state?.suggestedPlan;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch membership types
        const typesData = await getMembershipTypes();
        const types = typesData.results || typesData;
        setMembershipTypes(types);

        // Fetch current membership first
        let membershipData = null;
        try {
          membershipData = await getCurrentMembership();
          setCurrentMembership(membershipData);
        } catch (error) {
          console.log('No active membership found');
        }

        let isUpgrade = false;

        // Check if this is a Quarterly → Annual upgrade scenario
        if (membershipData?.membership_type?.name === 'Pro Member - Quarterly' &&
            (suggestedPlan?.name === 'Pro Member - Annual' || !suggestedPlan)) {
          try {
            const eligibilityData = await checkUpgradeEligibility();
            if (eligibilityData.eligible) {
              setIsQuarterlyToAnnualUpgrade(true);
              setUpgradeDetails(eligibilityData.upgrade_preview);
              isUpgrade = true;

              // Pre-fill auto-renewal from current quarterly membership
              if (membershipData?.auto_renew !== undefined) {
                setAutoRenew(membershipData.auto_renew);
              }

              // Set the Annual plan as selected
              const annualPlan = types.find(t => t.name === 'Pro Member - Annual');
              if (annualPlan) {
                setSelectedPlan(annualPlan);
              }
            }
          } catch (error) {
            console.log('Not eligible for quarterly upgrade:', error);
          }
        }

        // Check if this is an Annual → Family upgrade scenario. Unlike
        // Quarterly (which only ever upgrades to Annual), Annual could grow
        // more than one upgrade target in the future, so this requires an
        // explicit suggested plan rather than defaulting like Quarterly does.
        if (!isUpgrade && membershipData?.membership_type?.name === 'Pro Member - Annual' &&
            suggestedPlan?.name === 'Pro Member - Family') {
          try {
            const familyPlan = types.find(t => t.name === 'Pro Member - Family');
            const eligibilityData = await checkUpgradeEligibility(familyPlan?.id);
            if (eligibilityData.eligible) {
              setIsAnnualToFamilyUpgrade(true);
              setUpgradeDetails(eligibilityData.upgrade_preview);
              isUpgrade = true;

              if (membershipData?.auto_renew !== undefined) {
                setAutoRenew(membershipData.auto_renew);
              }

              if (familyPlan) {
                setSelectedPlan(familyPlan);
              }
            }
          } catch (error) {
            console.log('Not eligible for Annual -> Family upgrade:', error);
          }
        }
        // For Community→Pro upgrades, keep auto-renewal checked by default (initial state is true)

        // If not a prorated upgrade, use normal plan selection logic
        if (!isUpgrade) {
          if (suggestedPlan) {
            setSelectedPlan(suggestedPlan);
          } else {
            const proAnnual = types.find(type => type.name === 'Pro Member - Annual');
            if (proAnnual) {
              setSelectedPlan(proAnnual);
            }
          }
        }
      } catch (error) {
        setError('Failed to load upgrade options. Please try again.');
        console.error('Error loading memberships:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [suggestedPlan]);

  // Get Pro plans (quarterly and annual)
  const getProPlans = () => {
    const plans = membershipTypes.filter(type =>
      type.name.includes('Pro') && type.is_active
    );

    // During a prorated upgrade, only show the specific target plan. Note:
    // duration_months alone no longer identifies a plan uniquely (Annual
    // and Family are both 12 months), so match by name.
    if (isQuarterlyToAnnualUpgrade) {
      return plans.filter(p => p.name === 'Pro Member - Annual');
    }
    if (isAnnualToFamilyUpgrade) {
      return plans.filter(p => p.name === 'Pro Member - Family');
    }

    // Otherwise show all active Pro plans, sorted shortest term first
    return plans.sort((a, b) => a.duration_months - b.duration_months);
  };

  const proPlans = getProPlans();

  // Get the display price for a plan (considering upgrade discounts)
  const getDisplayPrice = (plan) => {
    if (isUpgradeFlow && plan.id === selectedPlan?.id && upgradeDetails) {
      return upgradeDetails.final_charge;
    }
    return plan.price;
  };
  
  // Calculate savings for annual plan
  const calculateSavings = () => {
    // Use membershipTypes instead of proPlans to ensure we have both quarterly and annual
    // even when proPlans is filtered (e.g., quarterly→annual upgrade shows only annual)
    const quarterly = membershipTypes.find(p => p.duration_months === 3 && p.name.includes('Pro'));
    const annual = membershipTypes.find(p => p.name === 'Pro Member - Annual');
    if (quarterly && annual) {
      return (quarterly.price * 4) - annual.price;
    }
    return 0;
  };

  const savings = calculateSavings();

  // Calculate next payment date
  const getNextPaymentDate = () => {
    if (!selectedPlan) return null;
    const months = selectedPlan.duration_months;
    const nextDate = new Date();
    nextDate.setMonth(nextDate.getMonth() + months);
    return nextDate.toLocaleDateString();
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  const handlePayment = async ({ stripe, elements, billingDetails, cardElement }) => {
    setProcessing(true);
    setPaymentError('');

    try {
      // Create payment intent
      const { client_secret, payment_id } = await createPaymentIntent({
        membership_type_id: selectedPlan.id,
        auto_renew: autoRenew
      });

      // Confirm payment with Stripe
      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: billingDetails.name,
            address: billingDetails.address,
          },
        }
      });

      if (result.error) {
        setPaymentError(result.error.message);
        setProcessing(false);
      } else {
        // Payment succeeded, confirm with backend
        const confirmResult = await confirmPayment({
          payment_intent_id: result.paymentIntent.id,
          payment_id: payment_id
        });

        if (confirmResult.success) {
          navigate('/dashboard', {
            state: {
              message: `Payment successful! You're now upgraded to ${selectedPlan.name}.`
            }
          });
        } else {
          setPaymentError('Payment confirmation failed. Please contact support.');
          setProcessing(false);
        }
      }
    } catch (err) {
      setPaymentError(err.message || 'An error occurred during payment processing.');
      setProcessing(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="max-w-md">
          <Alert type="error" message={error} onClose={() => setError('')} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isQuarterlyToAnnualUpgrade ? 'Upgrade to Annual Plan'
              : isAnnualToFamilyUpgrade ? 'Upgrade to Family Plan'
              : 'Upgrade to Pro'}
          </h1>
          <p className="text-lg text-gray-600 mt-2">
            {isQuarterlyToAnnualUpgrade
              ? 'Save money with annual billing and keep your billing anniversary.'
              : isAnnualToFamilyUpgrade
              ? 'Pro pricing for up to 4 family members per event, prorated for your current billing period.'
              : 'Unlock premium features and enhanced capabilities.'}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Plan Selection & Billing Details */}
          <div className="space-y-6">
            {/* Plan Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Choose Your Plan</h2>
              
              {proPlans.length > 0 && (
                <div className="space-y-3">
                  {proPlans.map(plan => (
                    <div
                      key={plan.id}
                      className={`relative rounded-lg border p-4 cursor-pointer transition-all ${
                        selectedPlan?.id === plan.id
                          ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handlePlanSelect(plan)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex items-center h-5">
                            <input
                              type="radio"
                              checked={selectedPlan?.id === plan.id}
                              onChange={() => handlePlanSelect(plan)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                          </div>
                          <div className="ml-3">
                            <div className="flex items-center">
                              <label className="block text-sm font-medium text-gray-900">
                                {plan.name}
                              </label>
                              {plan.name === 'Pro Member - Annual' && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Save ${savings}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{plan.description}</p>
                          </div>
                        </div>
                        {!isUpgradeFlow && (
                          <div className="text-right">
                            <div className="text-lg font-semibold text-gray-900">
                              ${plan.price}
                            </div>
                            <div className="text-sm text-gray-500">
                              per {plan.duration_months === 12 ? 'year' : 'quarter'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>

              {selectedPlan ? (
                <div className="space-y-4">
                  {isUpgradeFlow && upgradeDetails ? (
                    <>
                      {/* Upgrade Cost Breakdown */}
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">{selectedPlan.name}</span>
                          <span className="font-medium">${upgradeDetails.original_price.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-green-600">
                          <span>{isQuarterlyToAnnualUpgrade ? 'Quarterly Credit' : 'Proration Discount'}</span>
                          <span>-${upgradeDetails.credit.toFixed(2)}</span>
                        </div>
                        <div className="border-t pt-3">
                          <div className="flex justify-between font-semibold text-lg">
                            <span>Total Due Today</span>
                            <span className="text-blue-600">${upgradeDetails.final_charge.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Upgrade Info */}
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-sm text-gray-600">
                          <strong>Billing Period:</strong><br />
                          {new Date(upgradeDetails.new_period_start).toLocaleDateString()} - {new Date(upgradeDetails.new_period_end).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          Your original billing anniversary is preserved
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{selectedPlan.name}</span>
                        <span className="font-medium">${selectedPlan.price}</span>
                      </div>

                      <div className="border-t pt-4">
                        <div className="flex justify-between font-semibold text-lg">
                          <span>Total</span>
                          <span>${selectedPlan.price}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Select a plan to see order details</p>
              )}
            </div>

            {/* Auto-renewal & Next Payment */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Billing Details</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-900">Auto-renewal</label>
                    <p className="text-sm text-gray-500">Automatically renew subscription</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={autoRenew}
                      onChange={(e) => setAutoRenew(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
                
                {selectedPlan && autoRenew && (
                  <div className="pt-4 border-t">
                    <div className="text-sm text-gray-600">
                      <p><span className="font-medium">Next payment:</span> {
                        isUpgradeFlow && upgradeDetails
                          ? new Date(upgradeDetails.new_period_end).toLocaleDateString()
                          : getNextPaymentDate()
                      }</p>
                      <p><span className="font-medium">Amount:</span> ${selectedPlan.price}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Payment Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Payment Information</h3>
              {selectedPlan && (
                stripeKey ? (
                  <Elements stripe={stripePromise}>
                    <UpgradePaymentForm
                      selectedPlan={selectedPlan}
                      agreeToTerms={agreeToTerms}
                      setAgreeToTerms={setAgreeToTerms}
                      onSubmit={handlePayment}
                      processing={processing}
                      error={paymentError}
                    />
                  </Elements>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">Stripe Configuration Required</h3>
                        <p className="text-sm text-yellow-700 mt-1">
                          Please configure your Stripe publishable key in the .env file to enable payments.
                        </p>
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpgradePage;